import { Separator, Stack } from '@fluentui/react';
import { IEntitiesContainerVM, IEntityVM } from 'global/Compare/ComparisonViewModelGenerator';
import React from 'react';
import CompareNavText from '../Pharmacy/PharmacyPlan/CompareNavText';
import {
    IMarkerSet,
    QUICK_NAV_CHILD_STACK_STYLES,
    QUICK_NAV_CHILD_STACK_TOKENS,
    QUICK_NAV_GROUP_STYLE,
    QUICK_NAV_INDENT_STYLE,
    QUICK_NAV_SECTION_STACK_STYLES,
    QUICK_NAV_SECTION_STACK_TOKENS,
    QUICK_NAV_STACK_TOKENS,
    QUICK_NAV_STICKY_SECTION_TITLE_STYLE,
    QUICK_NAV_STICKY_SEGMENT_TITLE_STYLE,
} from './CompareQuickNavOverlay.styles';

// ITabVM/ISectionVM aren't exported from ComparisonViewModelGenerator; derive them from IEntityVM's shape
type ITabVM = IEntityVM['tabsVM'][number];
type ISectionVM = ITabVM['sectionsVM'][number];

// shared inputs every tree-node builder needs; bundled so each function only takes (data, context)
export interface IQuickNavTreeContext {
    markers: IMarkerSet;
    matchesSearch: (value?: string | null) => boolean;
    showDifferencesOnly: boolean;
    backgroundColor: string;
    onNavigateAsync: (links: string[]) => Promise<void>;
}

// attribute-set fields + non-standard entries within a section, flattened into a single node list
const buildSectionAttributeNodes = (
    section: ISectionVM,
    entityId: string,
    entityName: string,
    tabId: string,
    ctx: IQuickNavTreeContext,
): { nodes: React.ReactNode[]; hasVisibleNode: boolean } => {
    const nodes: React.ReactNode[] = [];
    let hasVisibleNode = false;

    section.attributeSetsVM.forEach((eachAttributeSetVM) => {
        eachAttributeSetVM.attributeSets.forEach((set, index) => {
            if (ctx.showDifferencesOnly && !set.changed) {
                return;
            }

            const label = set.friendlyName;
            if (!ctx.matchesSearch(label)) {
                return;
            }

            hasVisibleNode = true;
            const masterAttributeId = set.definition.bcbs_config_masterattribute.bcbs_config_masterattributeid!;
            nodes.push(
                <CompareNavText
                    markers={ctx.markers}
                    key={`${entityId}-${masterAttributeId}`}
                    changed={set.changed}
                    label={label}
                    onClick={() => ctx.onNavigateAsync([
                        entityName,
                        entityId,
                        `${entityId}-${tabId}`,
                        `${entityId}-${section.sectionDefinition.section.bcbs_config_sectionid!}`,
                        `${entityId}-${masterAttributeId}-${index}`,
                    ])}
                />
            );
        });
    });

    section.nonStandardsVM.forEach((nonStandardVM, nonStandardIndex) => {
        if (ctx.showDifferencesOnly && !nonStandardVM.changed) {
            return;
        }

        const nonStandardLabel = `Non-Standard → ${nonStandardVM.name}`;
        if (!ctx.matchesSearch(nonStandardLabel)) {
            return;
        }

        hasVisibleNode = true;
        const nonStandardSectionId = nonStandardVM.sectionDefinition.section.bcbs_config_sectionid!;
        nodes.push(
            <CompareNavText
                markers={ctx.markers}
                key={`${entityId}-${nonStandardSectionId}-nonstandard-${nonStandardIndex}`}
                changed={nonStandardVM.changed}
                label={nonStandardLabel}
                onClick={() => ctx.onNavigateAsync([
                    entityName,
                    entityId,
                    `${entityId}-${tabId}`,
                    `${entityId}-${nonStandardSectionId}`,
                    `${entityId}-${nonStandardSectionId}-nonstandard-${nonStandardIndex}`,
                ])}
            />
        );
    });

    return { nodes, hasVisibleNode };
};

const buildSectionNode = (
    section: ISectionVM,
    entityId: string,
    entityName: string,
    tabId: string,
    ctx: IQuickNavTreeContext,
): React.ReactNode | null => {
    if (ctx.showDifferencesOnly && !section.changed) {
        return null;
    }

    const sectionLabelMatches = ctx.matchesSearch(section.sectionDefinition.section.bcbs_name);
    const { nodes: attributeNodes, hasVisibleNode } = buildSectionAttributeNodes(section, entityId, entityName, tabId, ctx);

    if (!sectionLabelMatches && !hasVisibleNode) {
        return null;
    }

    const sectionId = section.sectionDefinition.section.bcbs_config_sectionid!;
    return (
        <Stack key={`${entityId}-section-${sectionId}`}>
            <CompareNavText
                markers={ctx.markers}
                changed={section.changed}
                label={section.sectionDefinition.section.bcbs_name}
                style={{
                    ...QUICK_NAV_STICKY_SECTION_TITLE_STYLE,
                    backgroundColor: ctx.backgroundColor,
                }}
                onClick={() => ctx.onNavigateAsync([
                    entityName,
                    entityId,
                    `${entityId}-${tabId}`,
                    `${entityId}-${sectionId}`,
                ])}
            />
            <Stack tokens={QUICK_NAV_SECTION_STACK_TOKENS} styles={QUICK_NAV_SECTION_STACK_STYLES}>
                {attributeNodes}
            </Stack>
        </Stack>
    );
};

const buildTabNode = (
    tab: ITabVM,
    entityId: string,
    entityName: string,
    ctx: IQuickNavTreeContext,
): React.ReactNode | null => {
    if (ctx.showDifferencesOnly && !tab.changed) {
        return null;
    }

    const tabLabelMatches = ctx.matchesSearch(tab.tabDefinition.tab.bcbs_name);
    const tabId = tab.tabDefinition.tab.bcbs_config_tabid!;

    const sectionNodes = tab.sectionsVM
        .map((section) => buildSectionNode(section, entityId, entityName, tabId, ctx))
        .filter((node): node is React.ReactNode => node !== null);

    if (!tabLabelMatches && sectionNodes.length === 0) {
        return null;
    }

    return (
        <Stack tokens={QUICK_NAV_SECTION_STACK_TOKENS} style={QUICK_NAV_INDENT_STYLE} key={`${entityId}-tab-${tabId}`}>
            <CompareNavText
                markers={ctx.markers}
                changed={tab.changed}
                label={tab.tabDefinition.tab.bcbs_name}
                style={{
                    position: 'sticky',
                    top: '132px',
                    paddingTop: '4px',
                    paddingBottom: '4px',
                    zIndex: 20,
                    backgroundColor: ctx.backgroundColor,
                }}
                onClick={() => ctx.onNavigateAsync([
                    entityName,
                    entityId,
                    `${entityId}-${tabId}`,
                ])}
            />
            <Stack tokens={QUICK_NAV_CHILD_STACK_TOKENS} styles={QUICK_NAV_CHILD_STACK_STYLES}>
                {sectionNodes}
            </Stack>
        </Stack>
    );
};

// builds the full nav tree (properties/references/tiers + tabs/sections/attributes) for a single entity
export const buildQuickNavEntityNode = (entity: IEntityVM, ctx: IQuickNavTreeContext): React.ReactNode | null => {
    if (ctx.showDifferencesOnly && !entity.changed) {
        return null;
    }

    const entityId = entity.uniqueId;
    const entityName = entity.context;
    const entityLabelMatches = ctx.matchesSearch(entity.friendlyName);

    const tabNodes = entity.tabsVM
        .map((tab) => buildTabNode(tab, entityId, entityName, ctx))
        .filter((node): node is React.ReactNode => node !== null);

    if (!entityLabelMatches && tabNodes.length === 0) {
        return null;
    }

    const showPropertiesNode = (entity.propertiesVM.rowsVM.length > 0)
        && (!ctx.showDifferencesOnly || !!entity.propertiesVM.changed)
        && ctx.matchesSearch('Properties');
    const showReferencesNode = ((entity.referencesVM?.rowsVM.length ?? 0) > 0)
        && (!ctx.showDifferencesOnly || !!entity.referencesVM?.changed)
        && ctx.matchesSearch('References');
    const showTiersNode = ((entity.tiersVM?.rowsVM.length ?? 0) > 0)
        && (!ctx.showDifferencesOnly || !!entity.tiersVM?.changed)
        && ctx.matchesSearch('Tiers');

    return (
        <Stack tokens={QUICK_NAV_STACK_TOKENS} style={QUICK_NAV_GROUP_STYLE} key={entityId}>
            <CompareNavText
                markers={ctx.markers}
                changed={entity.changed}
                label={entity.friendlyName ?? 'Entity'}
                style={{
                    position: 'sticky',
                    top: '102px',
                    padding: '8px',
                    zIndex: 30,
                    backgroundColor: ctx.backgroundColor,
                }}
                onClick={() => ctx.onNavigateAsync([entityName, entityId])}
            />
            {showPropertiesNode && (
                <CompareNavText
                    markers={ctx.markers}
                    changed={entity.propertiesVM.changed}
                    label={'Properties'}
                    style={QUICK_NAV_INDENT_STYLE}
                    onClick={() => ctx.onNavigateAsync([entityName, entityId, `${entityId}-properties`])}
                />
            )}
            {showReferencesNode && (
                <CompareNavText
                    markers={ctx.markers}
                    changed={entity.referencesVM?.changed}
                    label={'References'}
                    style={QUICK_NAV_INDENT_STYLE}
                    onClick={() => ctx.onNavigateAsync([entityName, entityId, `${entityId}-references`])}
                />
            )}
            {showTiersNode && (
                <CompareNavText
                    markers={ctx.markers}
                    changed={entity.tiersVM?.changed}
                    label={'Tiers'}
                    style={QUICK_NAV_INDENT_STYLE}
                    onClick={() => ctx.onNavigateAsync([entityName, entityId, `${entityId}-tiers`])}
                />
            )}
            {tabNodes}
        </Stack>
    );
};

// builds a top-level category (e.g. Plans, Benefits) with its separator, header, and entity nodes
export const buildQuickNavCategoryPanel = (
    list: IEntitiesContainerVM,
    ctx: IQuickNavTreeContext,
): React.ReactNode | null => {
    if (!list || (list.entities?.length ?? 0) === 0) {
        return null;
    }

    if (ctx.showDifferencesOnly && !list.changed) {
        return null;
    }

    const topLabelMatches = ctx.matchesSearch(list.title);
    const entityNodes = list.entities
        .map((entity) => buildQuickNavEntityNode(entity, ctx))
        .filter((node): node is React.ReactNode => node !== null);

    if (!topLabelMatches && entityNodes.length === 0) {
        return null;
    }

    return (
        <Stack key={list.context}>
            <Separator />
            <CompareNavText
                markers={ctx.markers}
                changed={list.changed}
                label={list.title + (list.entities.length > 0 ? ` (${list.entities.length})` : '')}
                style={{
                    ...QUICK_NAV_STICKY_SEGMENT_TITLE_STYLE,
                    backgroundColor: ctx.backgroundColor,
                }}
                onClick={() => ctx.onNavigateAsync([list.context ?? ''])}
            />
            {entityNodes}
        </Stack>
    );
};
